import { useStrings } from "@1js/localization-components";
import { Column, Grid, Row } from "@1js/pl-card-components";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import { FaqBody } from "./FaqBody.web";
import type { Faq, FaqsProps } from "./Faqs.web.data";
import { FaqTitle } from "./Faqs.web.string";

const useStyles = makeStyles({
  root: {
    minHeight: "350px",
    ...shorthands.padding("20px", "0px"),
  },

  title: {
    fontSize: "28px",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "36px",
    ...shorthands.margin("0px"),
    ...shorthands.gap("24px"),
    ...shorthands.padding("20px", "0px", "20px", "0px"),

    "@media (max-width: 540px)": {
      fontSize: "24px",
      lineHeight: "32px",
    },
  },

  subtitle: {
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "22px",
  },

  headline: {
    display: "flex",
  },

  qnaList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.gap("1.5rem"),
    ...shorthands.flex(1, 0, "0px"),
  },

  qnaHeader: {
    ...shorthands.margin("0px", "-16px"),
  },
});

export const Faqs: React.FC<FaqsProps> = ({ faqs }) => {
  const localizations = useStrings({
    FaqTitle,
  });

  const styles = useStyles();
  const { isFlightActive } = useFlightContext();

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <Grid
      className={styles.root}
      testid="faqs"
      goBold={isFlightActive("isGoBoldPromptCardEnabled")}
      isResponsive={isFlightActive("isResponsiveDimensionsEnabled")}
    >
      <Row>
        <Column size="auto" className={styles.headline}>
          <Text as="h2" className={styles.title}>
            {localizations.FaqTitle()}
          </Text>
        </Column>
      </Row>
      <Row>
        <Column size="auto">
          <Accordion className={styles.qnaList} collapsible>
            {faqs.map((faq: Faq, index: number) => (
              <AccordionItem value={index} key={faq.id}>
                <AccordionHeader className={styles.qnaHeader}>
                  <Text className={styles.subtitle} tabIndex={0}>
                    {faq.title}
                  </Text>
                </AccordionHeader>
                <AccordionPanel>
                  <FaqBody content={faq.content} />
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Column>
      </Row>
    </Grid>
  );
};
